import styled from "@emotion/styled";
import { FlexRow, IconCheckRounded } from "atoms";
import type { OpenCall, OpenCallTag } from "../types";
import { getOpenCallTag } from "../utils/getOpenCallTag";

type OpenCallTagsProps = {
  openCall: OpenCall;
  submitted?: boolean;
  voted?: boolean;
};

/**
 * Returns a styled status badge when given a status OR an openCall object
 */
function OpenCallTagBadge({ openCall, submitted, voted }: OpenCallTagsProps) {
  const tag = getOpenCallTag(openCall);
  const completed = !!(
    (submitted && tag === "Submissions Open") ||
    (voted && tag === "Voting Open")
  );

  return (
    <FlexRow gap="6px">
      <TagBadge tag={tag} completed={completed}>
        {completed && <IconCheckRounded />}
        {tag}
      </TagBadge>
    </FlexRow>
  );
}

/**
 * Styled element to display the Open Call's status
 */
const TagBadge = styled.span<{
  tag?: OpenCallTag;
  completed?: boolean;
}>(({ tag, completed, theme }) => ({
  ...((tag === "Coming Soon" || tag === "Voting Soon") && {
    backgroundColor: theme.colors.accent1LL,
  }),
  ...(tag === "Submissions Open" && {
    backgroundColor: completed ? theme.colors.fg10 : theme.colors.successL,
  }),
  ...(tag === "Voting Open" && {
    backgroundColor: completed ? theme.colors.fg10 : theme.colors.successL,
  }),
  ...(tag === "Under Curation" && {
    backgroundColor: theme.colors.accent2LL,
  }),
  ...(tag === "Artists Selected" && {
    backgroundColor: theme.colors.fg10,
  }),
  ...(tag === "On Display" && {
    backgroundColor: theme.colors.fg10,
  }),
  ...(tag === "Concluded" && {
    backgroundColor: theme.colors.fg10,
  }),

  ...theme.fonts.display,
  borderRadius: theme.borderRadius.xs,
  "&&": { color: theme.colors.black },
  display: "inline-flex",
  gap: 4,
  alignItems: "center",
  letterSpacing: ".02em",
  lineHeight: 1.2,
  fontSize: 12,
  margin: 0,
  padding: "3px .6em 2px",
  textDecoration: "none",
  width: "max-content",
  textTransform: "uppercase",

  svg: {
    width: "1em",
    height: "1em",
    padding: 1.5,
    borderRadius: theme.borderRadius.round,
    border: "1px solid",
    borderColor: "currentcolor",
    position: "relative",
    top: -0.5,
  },
}));

export { OpenCallTagBadge, TagBadge };
